import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBrand } from "@finitless/shared/contexts";
import { useUIStore } from "@finitless/shared/stores";
import { useSocketIdSync } from "../hooks/useSocketIdSync";
import { useOrderNotifications } from "../hooks/useOrderNotifications";
import { usePastDueBannerVisible } from "../hooks/useBilling";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import { PastDueBanner, PlanUpgradeModal, PAST_DUE_BANNER_HEIGHT } from "./ui";
import EmptyBusinessState from "./ui/States/EmptyBusinessState";
import BusinessLoadingState from "./ui/States/BusinessLoadingState";
import OrderNotificationToast from "./ui/Orders/OrderNotificationToast";

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { initializeTheme } = useUIStore();

  // Sync socket ID with API client
  useSocketIdSync();

  // Global order notifications
  const { notifications, removeNotification } = useOrderNotifications();

  const {
    canShowBusinessContent,
    getBusinessState,
    isLoading: businessLoading,
    handleRetry,
  } = useBrand();

  const isProfilePage = location.pathname === "/account";
  const isBusinessCreationPage = location.pathname.startsWith(
    "/menu/stores/create",
  );

  // Initialize theme on component mount
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  useEffect(() => {
    const mainContent = document.querySelector("main");
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Get current page from URL path
  const currentPage = location.pathname.substring(1) || "overview";

  // Para móvil: mostrar/ocultar sidebar
  const toggleMobileSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Para desktop: colapsar/expandir sidebar
  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const showPastDueBanner = usePastDueBannerVisible();

  return (
    <div className="bg-background min-h-screen">
      {/* Past Due banner - fixed above header, only when past_due */}
      <PastDueBanner />

      {/* Plan Upgrade modal - for Upgrade to Pro flow */}
      <PlanUpgradeModal />

      <div
        className="flex h-screen transition-[padding] duration-200"
        style={{
          paddingTop: showPastDueBanner ? PAST_DUE_BANNER_HEIGHT : 0,
        }}
      >
        {/* Sidebar */}
        <div
          className={`bg-sidebar border-border fixed inset-y-0 start-0 z-50 transform border-e transition-all duration-300 ease-in-out lg:static lg:inset-0 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} ${isSidebarCollapsed ? "lg:w-16" : "lg:w-56"} w-56`}
        >
          <Sidebar
            onClose={closeSidebar}
            currentPage={currentPage}
            isCollapsed={isSidebarCollapsed}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden lg:ml-0">
          {/* Top Bar */}
          <TopBar
            onToggleSidebar={toggleSidebarCollapse}
            onToggleMobileSidebar={toggleMobileSidebar}
          />

          {/* Main Content */}
          <main className="bg-background flex-1 overflow-y-auto">
            <div className="p-3 sm:p-4">
              {isProfilePage || isBusinessCreationPage ? (
                children
              ) : canShowBusinessContent ? (
                children
              ) : getBusinessState === "loading" ? (
                <BusinessLoadingState />
              ) : (
                <EmptyBusinessState
                  onRetry={handleRetry}
                  isLoading={businessLoading}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="bg-background bg-opacity-50 fixed inset-0 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Global Order Notifications */}
      {notifications.map((notification) => (
        <OrderNotificationToast
          key={notification.id}
          order={notification.order}
          isVisible={notification.isVisible}
          onClose={() => removeNotification(notification.id)}
          duration={5000}
          position="top-right"
          type={notification.type}
        />
      ))}
    </div>
  );
};

export default AppLayout;
